import React, { useEffect, useState } from "react";
import stateResolver from "../../../data/utils/stateUtil";
import { merge } from "../../Utils";
import { useSetRecoilState } from "recoil";
import tableState from "../../SimpleForm/Atoms/Table/state";
import getMappingSchema from "../_Utils/getMappingSchema";

export function usePWDs({
  formType,
  formApi,
  is_emergency,
  worksiteFields = {},
  PWFields: { extraFieldsToSetPrimaryPW, PWLinkingFields },
}) {
  const [PWs, setPWs] = useState([]);
  const formValues = formApi.getState().values;

  const { importedWorksites, chosenWorksites, bls_counties } = worksiteFields;

  // If primary PW is cleared elsewhere, re-pull all pw data from formApi
  const primaryPWFormValue = formValues[PWLinkingFields[0]];
  useEffect(() => {
    // Get linked PWs
    const savedPWs = [];
    PWLinkingFields.forEach((field) => {
      const caseNumber = formValues[field];
      caseNumber && savedPWs.push(caseNumber);
    });
    setPWs(savedPWs);
  }, [primaryPWFormValue]);

  const setAppxAErrors = useSetRecoilState(tableState);

  function getNewWorksiteLocations({ data }) {
    // A lot of this worksites logic is shared in pwPrefiller.js. Would be a good mini-refactor.

    let new_locations = [];
    // adding primary worksite no matter what
    new_locations.push({
      worksiteCity: data.primaryWorksiteCity || null,
      worksiteState: data.primaryWorksiteState || null,
      worksiteCountyTownship: data.primaryWorksiteCounty?.label || null,
      worksiteBlsArea: data.primaryWorksiteBlsArea
        ? data.primaryWorksiteBlsArea.toUpperCase()
        : null,
      determinationDate: data.determinationDate || null,
      dateSubmitted: data.dateSubmitted || null,
      pwdUnitOfPay: data.pwUnitOfPay || null,
      pwdWageRate: data?.pwdWageRate?.toString() || null,
      rowNum: data?.rowNum?.toString() || new Date().getTime().toString(),
    });

    return new_locations;
  }
  function getNewBlsCounties({ data }) {
    let bls_counties = {};
    // adding primary worksite no matter what
    if (data._blsCounties) {
      bls_counties = JSON.parse(data._blsCounties);
    }

    return bls_counties;
  }

  // Maps worksite data from a Prevailing Wage to the case
  function addPW(caseNumber, data) {
    // A lot of this worksites logic is shared in pwPrefiller.js. Would be a good mini-refactor.
    const primaryPWField = PWLinkingFields[0];
    const isPrimaryPWLinked = PWs[0];
    // If primary PW isn't linked, add primary PW
    if (!isPrimaryPWLinked) {
      setPWs([caseNumber]);
      extraFieldsToSetPrimaryPW &&
        extraFieldsToSetPrimaryPW.forEach((field) =>
          formApi.change(field, caseNumber)
        );
      formApi.change(primaryPWField, caseNumber);
      // else, add additional PWs
    } else {
      const latestFormValues = formApi.getState().values;

      // Add worksites
      const existing_locations = latestFormValues[chosenWorksites] || [];
      const latestBlsCounties = bls_counties
        ? latestFormValues[bls_counties]
        : null;
      const existing_bls_counties = latestBlsCounties
        ? JSON.parse(latestBlsCounties)
        : {};

      const otherWorksites = data.otherWorksites;
      const incoming_locations =
        typeof otherWorksites === "string"
          ? JSON.parse(otherWorksites)
          : otherWorksites;
      const new_locations = getNewWorksiteLocations({ data });
      const new_bls_counties = getNewBlsCounties({ data });

      incoming_locations.forEach((worksite) => {
        let { worksiteState } = worksite;
        if (worksiteState) {
          if (typeof worksite.worksiteState === "number") {
            worksite.worksiteState = stateResolver(worksite.worksiteState);
          }
          // adding new properties switch to datahub
          worksite = {
            determinationDate: data.caseMetadata.determinationDate || null,
            dateSubmitted: data.dateSubmitted || null,
            worksiteCity: worksite.worksiteCity || null,
            worksiteBlsArea: worksite.worksiteBlsArea
              ? worksite.worksiteBlsArea.toUpperCase()
              : null,
            worksiteCountyTownship: worksite.worksiteCountyTownship || null,
            worksiteState: worksite.worksiteState || null,
            worksitePostalCode: worksite.worksitePostalCode || null,
            pwdUnitOfPay: worksite.pwUnitOfPay || null,
            pwdWageRate: worksite?.prevailingWage?.toString() || null,
            rowNum:
              worksite?.rowNum?.toString() || new Date().getTime().toString(),
          };
          new_locations.push(worksite);
        }
      });
      let merged_bls_counties = merge(existing_bls_counties, new_bls_counties);

      const combined_worksites = existing_locations.concat(new_locations);
      const combined_bls_counties = JSON.stringify(merged_bls_counties);

      formApi.change(chosenWorksites, combined_worksites);
      if (bls_counties) {
        formApi.change(bls_counties, combined_bls_counties);
      }

      let savedPWs = [...PWs, caseNumber];
      setPWs(savedPWs);
      PWLinkingFields.forEach((field, index) => {
        formApi.change(PWLinkingFields[index], savedPWs[index] || null);
      });
    }
  }

  function clearAppxAErrors() {
    setAppxAErrors((errors) => {
      let newTableErrors = JSON.parse(JSON.stringify(errors));

      if (Object.keys(errors).length) {
        Object.keys(errors).forEach((key) => {
          if (key === importedWorksites) {
            newTableErrors[`${key}`] = {};
          }
        });
      }

      return newTableErrors;
    });
  }

  function handleDelete(e, deleteIndex) {
    const isPrimaryPW = deleteIndex === 0;

    let newPWs = isPrimaryPW
      ? []
      : PWs.filter((PW, i) => {
          return i !== deleteIndex;
        });

    setPWs(newPWs);
    PWLinkingFields.forEach((field, i) => {
      formApi.change(field, newPWs[i] || null);
    });

    if (isPrimaryPW) {
      clearAppxAErrors();

      // This is a special field for 9142B for importing multiple fields into 1
      if (formType === "9142B") {
        formApi.change("jobMinspecialreq", null);
      }

      if (formType === "9142C") {
        if (is_emergency === "NO") {
          formApi.change("a7_9141_attached", "N/A");
        } else {
          formApi.change("a7_9141_attached", "NO");
        }
      }

      if (formType === "9089") {
        formApi.change("primaryWorksiteBlsAreaCode", null);
        formApi.change("primaryWorksiteAddr1", null);
        formApi.change("primaryWorksiteAddr2", null);
        formApi.change("primaryWorksiteCity", null);
        formApi.change("primaryWorksiteCounty", null);
        formApi.change("primaryWorksiteState", null);
        formApi.change("primaryWorksitePostalCode", null);
        formApi.change("primaryWorksiteBlsArea", null);
        formApi.change("pwdHasMultipleWorksites", false);
        formApi.change("isMultipleLocations", null);
        formApi.change("faWorksiteLinked", false);
        formApi.change("bargainingRep", 0);
        formApi.change("appendixABRequired", false);
        formApi.change("appendixACRequired", false);
      }

      if (formType !== "9035") {
        formApi.change(chosenWorksites, []);
        formApi.change(importedWorksites, []);
      }

      extraFieldsToSetPrimaryPW &&
        extraFieldsToSetPrimaryPW.forEach((field) =>
          formApi.change(field, null)
        );
      // POC CLEAR
      const { schemaPOC, schemaRest, schemaRestV2 } =
        getMappingSchema(formType);

      schemaPOC.forEach((mapping) => {
        const mapToField = mapping["to_e"];
        if (mapToField) {
          formApi.change(mapToField, null);
        }
      });
      // CLEAR FOR THE REST (SECTIONS B, E, D)
      schemaRest.forEach((mapping) => {
        const mapToField = mapping["to"];
        if (mapToField) {
          formApi.change(mapToField, null);
        }
      });
      schemaRestV2.forEach((mapping) => {
        const mapToField = mapping["to"];
        if (mapToField) {
          formApi.change(mapToField, null);
        }
      });
    }
  }

  const renderPWDs = () => {
    return PWs.map((PW, index) => {
      let tagText = index === 0 ? "Primary PW" : `Additional PW ${index + 1}`;
      return (
        <div style={{ marginTop: "1rem" }}>
          <span className="usa-tag">{tagText}</span>
          <strong>{PW}</strong>
          <span
            tabIndex={0}
            onClick={(e) => handleDelete(e, index)} // TODO
            onKeyUp={(e) => {
              if (e.key === " ") handleDelete(e, index);
            }}
            className="button-link"
            style={{ marginLeft: "1rem" }}
          >
            Clear
          </span>
        </div>
      );
    });
  };

  return {
    renderPWDs,
    addPW,
    PWs,
  };
}
